import {
  ClockIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

import {
  Frozen as FrozenIcon,
  Start as StarIcon,
} from '@/components/chariot-ui';
import {
  StatusChip,
  StatusChipProps,
  StatusChipVariant,
} from '@/components/ui/status-chip';
import { Asset, AssetStatus, AWSResource, Port, Webpage } from '@/types';
import { cn } from '@/utils/classname';
import {
  assetStatusVariants,
  getVariantForStatus,
} from '@/utils/statusChip.util';

const hostIntegrationNames: Record<string, string> = {
  'github.com': 'GitHub',
  'gitlab.com': 'GitLab',
  'bitbucket.org': 'Bitbucket',
  'dev.azure.com': 'Azure DevOps',
};

function getRequiredIntegrationName(
  asset: Asset | Webpage | AWSResource | Port
): string {
  const url =
    ('group' in asset ? (asset as Asset).group : '') ||
    ('dns' in asset ? (asset as Asset).dns : '') ||
    '';
  for (const [host, name] of Object.entries(hostIntegrationNames)) {
    if (url.includes(host)) return name;
  }
  return 'the required integration';
}

export function AssetStatusComponent(props: {
  asset: Asset | Webpage | AWSResource | Port;
  className?: string;
}) {
  const { asset, className } = props;

  const assetStatusProps = getAssetStatusProps(asset);

  if (!assetStatusProps) return null;

  return <StatusChip {...assetStatusProps} className={cn(className)} />;
}

interface AssetStatusChipProps extends StatusChipProps {
  variant: StatusChipVariant;
}

const getAssetStatusProps = (
  asset: Asset | Webpage | AWSResource | Port
): AssetStatusChipProps | null => {
  if (!asset?.status) return null;
  // 'FR' (FrozenRejected, displayed as "Rejected") must be matched as a full
  // string BEFORE the prefix branches below, otherwise 'FR'[0] === 'F' would
  // incorrectly fall into the AssetStatus.Frozen branch. There is no
  // FrozenRejected member on the AssetStatus enum yet (a follow-up feature adds
  // first-class FR support), so the raw string literal is used here.
  if (asset.status === 'FR') {
    return {
      label: 'Rejected',
      variant: getVariantForStatus('rejected', assetStatusVariants),
      icon: <XCircleIcon className="size-3 shrink-0" />,
      tooltip: 'Asset has been rejected',
    };
  }
  if (asset.status[0] === AssetStatus.Active) {
    return {
      label: 'Approved',
      variant: getVariantForStatus('active', assetStatusVariants),
      icon: <StarIcon className="size-3 shrink-0" />,
      tooltip: 'Asset is approved and active',
    };
  } else if (asset.status[0] === AssetStatus.Frozen) {
    return {
      label: 'Frozen',
      variant: getVariantForStatus('frozen', assetStatusVariants),
      icon: <FrozenIcon className="size-3 shrink-0" />,
    };
  } else if (asset.status[0] === AssetStatus.Deleted) {
    return {
      label: 'Deleted',
      variant: getVariantForStatus('deleted', assetStatusVariants),
      icon: <TrashIcon className="size-3 shrink-0" />,
    };
  } else if (asset.status[0] === AssetStatus.Pending) {
    return {
      label: 'Pending',
      variant: getVariantForStatus('pending', assetStatusVariants),
      icon: <ClockIcon className="size-3 shrink-0" />,
    };
  } else if (asset.status[0] === AssetStatus.Unavailable) {
    const integrationName = getRequiredIntegrationName(asset);
    return {
      label: 'Unavailable',
      variant: getVariantForStatus('unavailable', assetStatusVariants),
      icon: <ExclamationTriangleIcon className="size-3 shrink-0" />,
      tooltip: `Configure ${integrationName} to provide Guard access to this asset`,
    };
  }
  return null;
};
